import { useEffect, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { saveAs } from "file-saver";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloseIcon from "@mui/icons-material/Close";

import {
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import MonitoringIcon from "./monitoring-icon.svg";
import SuccessIcon from "./success-icon.svg";
import FailureIcon from "./failure-icon.svg";

import { useTrueBizApi } from "../../../../api";
import { useIdentity } from "../../../IdentityProvider";

export interface Props {
  onCancel: () => void;
  onDone: () => void;
}

export default function MonitorCSVEnroll({ onCancel, onDone }: Props) {
  const theme = useTheme();
  const api = useTrueBizApi();
  const { impersonatorUserId } = useIdentity();

  const [working, setWorking] = useState(false);
  const [currentState, setCurrentState] = useState<
    "upload" | "success" | "error"
  >("upload");

  const onUploadCsv = useCallback(
    async (file: File) => {
      try {
        setWorking(true);

        await api.initiateMonitoringEnrollmentsImport({
          uploadFile: file,
          emailOnSuccess: true,
          impersonatorUserId: impersonatorUserId || null,
        });

        setWorking(false);
        setCurrentState("success");
      } catch (e) {
        setCurrentState("error");
      } finally {
        setWorking(false);
      }
    },
    [api, impersonatorUserId]
  );

  const onDownloadTemplate = async () => {
    try {
      setWorking(true);

      const templateString =
        "domain,external_ref_id,risk_profile,comments\n" +
        "example.com,00000000-0000-0000-0000-000000000000,North America,leave risk_profile blank if you do not need to customize the profile; leave external_ref_id blank if you do not wish to associate an external id\n" +
        "example.net,cus_NffrFeUfNV2Hib,Global,leave risk_profile blank if you do not need to customize the profile; leave external_ref_id blank if you do not wish to associate an external id\n" +
        "example.io,,,leave risk_profile blank if you do not need to customize the profile; leave external_ref_id blank if you do not wish to associate an external id\n" +
        "\n";

      const template = new Blob([templateString], {
        type: "text/csv",
      });

      saveAs(template, "truebiz-bulk-enrollments-template.csv");
    } catch (e) {
      setCurrentState("error");
    } finally {
      setWorking(false);
    }
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    disabled: working,
    maxFiles: 1,
  });

  useEffect(() => {
    if (!acceptedFiles?.length) return;
    const [csvFile] = acceptedFiles;
    onUploadCsv(csvFile);
  }, [acceptedFiles, onUploadCsv]);

  if (currentState === "success") {
    return (
      <Stack
        style={{
          width: "50vw",
          maxWidth: "425px",
          paddingLeft: "0.75em",
          paddingRight: "0.75em",
          paddingBottom: "0.75em",
          display: "flex",
          flex: 1,
        }}
      >
        <h2>
          <IconButton
            onClick={() => {
              onDone();
            }}
            disabled={working}
          >
            <CloseIcon />
          </IconButton>
          CSV Upload
        </h2>
        <Stack
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "6em",
          }}
        >
          <img src={SuccessIcon} width={175} alt="" />
          <h3 style={{ color: "#66996C" }}>Import started</h3>
          You will receive an email with confirmation once your import has been
          processed.
        </Stack>
      </Stack>
    );
  }

  if (currentState === "error") {
    return (
      <Stack
        style={{
          width: "50vw",
          maxWidth: "425px",
          paddingLeft: "0.75em",
          paddingRight: "0.75em",
          paddingBottom: "0.75em",
          display: "flex",
          flex: 1,
        }}
      >
        <h2>
          <IconButton
            onClick={() => {
              onCancel();
            }}
            disabled={working}
          >
            <CloseIcon />
          </IconButton>
          CSV Upload
        </h2>
        <Stack
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "6em",
          }}
        >
          <img src={FailureIcon} width={175} alt="" />
          <h3 style={{ textAlign: "center", color: "#CC3333" }}>
            Processing Error
          </h3>
          <Typography
            color="error"
            sx={{ mb: 3, maxWidth: "75%", textAlign: "center" }}
          >
            There was an error uploading your file. Please try again shortly.
          </Typography>
          <Button
            onClick={() => {
              setCurrentState("upload");
            }}
          >
            Retry
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      style={{
        width: "50vw",
        maxWidth: "425px",
        paddingLeft: "0.75em",
        paddingRight: "0.75em",
        paddingBottom: "0.75em",
        display: "flex",
        flex: 1,
      }}
    >
      <h2>
        <IconButton
          onClick={() => {
            onCancel();
          }}
          disabled={working}
        >
          <CloseIcon />
        </IconButton>
        CSV Upload
      </h2>
      <Stack
        sx={{
          alignItems: "center",
          mb: "2em",
        }}
      >
        <img src={MonitoringIcon} width={100} alt="" />
        <Typography
          sx={{
            textAlign: "center",
            mb: 2,
          }}
        >
          You can enroll or update multiple <br />
          merchants by uploading a CSV.
          <br />
        </Typography>
        <Box
          sx={{
            mt: 3,
          }}
        >
          <div
            style={{
              height: "2em",
              width: "2em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              backgroundColor: theme.palette.primary.main,
              borderRadius: "50%",
            }}
          >
            1
          </div>
        </Box>
        <Stack sx={{ mt: 1 }}>
          <Typography>
            <LoadingButton
              disabled={working}
              onClick={onDownloadTemplate}
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
            >
              Download the CSV template
            </LoadingButton>
          </Typography>
        </Stack>
        <Box
          sx={{
            mt: 5,
          }}
        >
          <div
            style={{
              height: "2em",
              width: "2em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              backgroundColor: theme.palette.primary.main,
              borderRadius: "50%",
            }}
          >
            2
          </div>
        </Box>
        <Stack
          {...getRootProps()}
          sx={{
            textAlign: "center",
            mt: 3,
            border: `2px dashed ${theme.palette.grey[400]}`,
            background: theme.palette.grey[100],
            width: "100%",
            borderRadius: "5px",
          }}
        >
          <input {...getInputProps()} />
          <Stack
            sx={{
              padding: 4,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                color: working
                  ? theme.palette.grey[300]
                  : theme.palette.grey[600],
              }}
            >
              <strong>
                Drag and drop your <br /> filled CSV template here
              </strong>
            </Typography>
            <LoadingButton sx={{ mt: 3 }} loading={working}>
              Select a CSV file
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
